import { useCallback, useEffect, useRef, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import {
  api,
  ApiRequestError,
  type MediaAsset,
  type SocialAccount,
  type SocialPolicy,
  type SocialPublication,
  type SocialComment,
} from '../lib/api';
import { Logo } from './Logo';
import { scheduledInstant } from '../lib/schedule-time';
import './social.css';

const names: Record<SocialAccount['platform'], string> = {
  instagram: 'Instagram',
  facebook: 'Facebook',
  tiktok: 'TikTok',
};
const capabilityNames: Record<string, string> = {
  image: 'Imagem',
  carousel: 'Carrossel',
  story: 'Story',
  reel: 'Reel',
  draft: 'Rascunho',
  direct: 'Publicação direta',
  photoDraft: 'Foto para rascunho',
  photoDirect: 'Foto direta',
  photoCarouselDraft: 'Carrossel para rascunho',
  photoCarouselDirect: 'Carrossel direto',
  metrics: 'Métricas',
  comments: 'Comentários',
};
const statusNames: Record<string, string> = {
  AVAILABLE: 'Disponível',
  UNAVAILABLE: 'Indisponível',
  REQUIRES_CONFIGURATION: 'Requer configuração',
  REQUIRES_APPROVAL: 'Requer aprovação',
  REQUIRES_ACTION: 'Requer ação manual',
  READY: 'Agendada',
  PROCESSING: 'Processando',
  PUBLISHED: 'Publicada',
  FAILED: 'Falhou',
  RETRYING: 'Nova tentativa',
  CANCELLED: 'Cancelada',
  DISPATCHING: 'Enviando',
};
type CreatorInfo = Awaited<
  ReturnType<typeof api.social.creatorInfo>
>['creatorInfo'];

const policyToggles: Array<[keyof SocialPolicy, string]> = [
  ['publishing_enabled', 'Permitir publicação externa'],
  ['allow_direct', 'Permitir envio direto'],
  ['allow_draft', 'Permitir rascunho'],
  ['require_manual_review', 'Exigir preparação manual'],
  ['allow_image', 'Permitir foto'],
  ['allow_carousel', 'Permitir carrossel'],
  ['allow_story', 'Permitir Story'],
  ['allow_video', 'Permitir vídeo'],
  ['metrics_enabled', 'Coletar métricas periodicamente'],
];

function PolicyEditor({
  account,
  busy,
  onSave,
}: {
  account: SocialAccount;
  busy: boolean;
  onSave: (policy: SocialPolicy) => Promise<void>;
}) {
  const [draft, setDraft] = useState(account.policy);
  useEffect(() => setDraft(account.policy), [account.policy]);
  return (
    <details>
      <summary>Política desta conta</summary>
      <p>
        As restrições são verificadas ao agendar e novamente antes de acessar a
        plataforma.
      </p>
      {policyToggles
        .filter(
          ([key]) =>
            (key !== 'allow_draft' || account.platform === 'tiktok') &&
            (key !== 'allow_story' || account.platform === 'instagram') &&
            (key !== 'allow_carousel' || account.platform !== 'facebook'),
        )
        .map(([key, label]) => (
          <label className="social-check" key={key}>
            <input
              type="checkbox"
              checked={Boolean(draft[key])}
              onChange={(event) =>
                setDraft({ ...draft, [key]: event.target.checked })
              }
            />
            {label}
          </label>
        ))}
      <label>
        Antecedência mínima (minutos)
        <input
          type="number"
          min={1}
          max={1440}
          value={draft.minimum_lead_minutes}
          onChange={(event) =>
            setDraft({
              ...draft,
              minimum_lead_minutes: Number(event.target.value),
            })
          }
        />
      </label>
      <label>
        Intervalo de métricas (horas)
        <input
          type="number"
          min={6}
          max={168}
          value={draft.metrics_interval_hours}
          onChange={(event) =>
            setDraft({
              ...draft,
              metrics_interval_hours: Number(event.target.value),
            })
          }
        />
      </label>
      <button
        className="secondary"
        disabled={busy}
        onClick={() => void onSave(draft)}
      >
        Salvar política
      </button>
    </details>
  );
}

export function SocialWorkspace({
  session,
  onBack,
}: {
  session: Session;
  onBack: () => void;
}) {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [assets, setAssets] = useState<MediaAsset[]>([]);
  const [publications, setPublications] = useState<SocialPublication[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [assetId, setAssetId] = useState('');
  const [readinessAssetId, setReadinessAssetId] = useState('');
  const [readiness, setReadiness] = useState<{
    data: Awaited<ReturnType<typeof api.social.instagramReadiness>>;
    mediaCheck: {
      httpStatus: number;
      mimeMatches: boolean;
      bytesMatch: boolean;
      sha256Matches: boolean;
      expiredHttpStatus: number;
    } | null;
  } | null>(null);
  const [caption, setCaption] = useState('');
  const [masterDate, setMasterDate] = useState('');
  const [masterClock, setMasterClock] = useState('');
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo',
  );
  const [tiktokMode, setTikTokMode] = useState<'DIRECT' | 'DRAFT' | 'MANUAL'>(
    'DRAFT',
  );
  const [privacy, setPrivacy] = useState('');
  const [consent, setConsent] = useState(false);
  const [metaTestConsent, setMetaTestConsent] = useState(false);
  const [disableComment, setDisableComment] = useState(false);
  const [disableDuet, setDisableDuet] = useState(false);
  const [disableStitch, setDisableStitch] = useState(false);
  const [creatorInfo, setCreatorInfo] = useState<CreatorInfo | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<
    Record<
      string,
      Array<{
        metric_name: string;
        provider_value: number;
        captured_at: string;
      }>
    >
  >({});
  const [comments, setComments] = useState<Record<string, SocialComment[]>>({});
  const batchKey = useRef(crypto.randomUUID());
  async function checkInstagramReadiness(accountId: string) {
    if (!readinessAssetId) return;
    setBusy(true);
    setError(null);
    setReadiness(null);
    let stage = 'api';
    try {
      const data = await api.social.instagramReadiness(
        session,
        accountId,
        readinessAssetId,
      );
      setReadiness({ data, mediaCheck: null });
      if (!data.media) return;
      stage = 'signed_media_fetch';
      const response = await fetch(data.media.url, { cache: 'no-store' });
      if (!response.ok) {
        setError(
          `Diagnóstico: SIGNED_MEDIA_HTTP_ERROR · HTTP ${response.status}. Nenhuma publicação foi criada.`,
        );
        return;
      }
      stage = 'signed_media_body';
      const bytes = await response.arrayBuffer();
      stage = 'signed_media_digest';
      const digest = await crypto.subtle.digest('SHA-256', bytes);
      const sha256 = Array.from(new Uint8Array(digest))
        .map((part) => part.toString(16).padStart(2, '0'))
        .join('');
      stage = 'expired_media_fetch';
      const expired = await fetch(data.media.expiredUrl, {
        cache: 'no-store',
      });
      setReadiness({
        data,
        mediaCheck: {
          httpStatus: response.status,
          mimeMatches:
            response.headers.get('content-type')?.split(';')[0] ===
            data.media.expectedMime,
          bytesMatch: bytes.byteLength === data.media.expectedBytes,
          sha256Matches: sha256 === data.media.expectedSha256,
          expiredHttpStatus: expired.status,
        },
      });
    } catch (cause) {
      const detail =
        cause instanceof ApiRequestError
          ? `${cause.code}${cause.stage ? ` · etapa ${cause.stage}` : ''}${cause.httpStatus === null ? '' : ` · HTTP ${cause.httpStatus}`}${cause.requestId ? ` · solicitação ${cause.requestId}` : ''}`
          : `${stage.toUpperCase()}_FAILED`;
      setError(`Diagnóstico: ${detail}. Nenhuma publicação foi criada.`);
    } finally {
      setBusy(false);
    }
  }
  const load = useCallback(async () => {
    const [connections, library, history] = await Promise.all([
      api.social.accounts(session),
      api.media.assets(session),
      api.social.publications(session),
    ]);
    setAccounts(connections.accounts);
    setAssets(
      library.assets.filter(
        (asset) =>
          asset.media_role === 'FINAL' &&
          asset.validation_status === 'VALIDATED' &&
          (asset.asset_type !== 'CAROUSEL' || asset.metadata.page === 0),
      ),
    );
    setPublications(history.publications);
  }, [session]);
  useEffect(() => {
    void load().catch((cause: unknown) =>
      setError(
        cause instanceof Error
          ? cause.message
          : 'Falha ao carregar publicações.',
      ),
    );
  }, [load]);
  useEffect(() => {
    const tikTok = accounts.find(
      (account) =>
        selected.includes(account.id) && account.platform === 'tiktok',
    );
    if (!tikTok || tiktokMode !== 'DIRECT') {
      setCreatorInfo(null);
      return;
    }
    void api.social
      .creatorInfo(session, tikTok.id)
      .then((data) => {
        setCreatorInfo(data.creatorInfo);
        setPrivacy(data.creatorInfo.privacy_level_options[0] ?? '');
        setDisableComment(data.creatorInfo.comment_disabled);
        setDisableDuet(data.creatorInfo.duet_disabled);
        setDisableStitch(data.creatorInfo.stitch_disabled);
      })
      .catch((cause: unknown) =>
        setError(
          cause instanceof Error
            ? cause.message
            : 'Não foi possível consultar as opções do TikTok.',
        ),
      );
  }, [accounts, selected, session, tiktokMode]);
  const asset = assets.find((item) => item.id === assetId);
  const tiktokSelected = accounts.some(
    (account) => selected.includes(account.id) && account.platform === 'tiktok',
  );
  const metaTestSelected = accounts.some(
    (account) =>
      selected.includes(account.id) && account.development_publish_test,
  );

  async function act(task: () => Promise<unknown>, message: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const result = await task();
      setNotice(typeof result === 'string' ? result : message);
      await load();
    } catch (cause) {
      setError(
        cause instanceof Error ? cause.message : 'Operação não concluída.',
      );
    } finally {
      setBusy(false);
    }
  }
  async function connect(service: SocialAccount['platform']) {
    await act(async () => {
      const data = await api.social.connect(session, service);
      window.location.assign(data.authorizationUrl);
    }, 'Redirecionando para a autorização oficial.');
  }
  async function schedule() {
    await act(async () => {
      if (!asset || !selected.length)
        throw new Error('Selecione um asset final e pelo menos uma conta.');
      const masterScheduledAt = scheduledInstant(
        masterDate,
        masterClock,
        timezone,
      );
      if (Date.parse(masterScheduledAt) < Date.now() + 60_000)
        throw new Error(
          'Escolha um horário futuro com pelo menos um minuto de antecedência.',
        );
      const outcomes: string[] = [];
      let failed = false;
      for (const accountId of selected) {
        const account = accounts.find((item) => item.id === accountId)!;
        const tiktokPhoto =
          account.platform === 'tiktok' &&
          ['image/jpeg', 'image/webp'].includes(asset.mime_type) &&
          asset.asset_type !== 'STORY';
        const carousel = asset.asset_type === 'CAROUSEL';
        const feature =
          account.platform === 'tiktok'
            ? tiktokPhoto
              ? tiktokMode === 'DRAFT'
                ? carousel
                  ? 'photoCarouselDraft'
                  : 'photoDraft'
                : carousel
                  ? 'photoCarouselDirect'
                  : 'photoDirect'
              : tiktokMode === 'DRAFT'
                ? 'draft'
                : 'direct'
            : account.platform === 'facebook' &&
                ['image/png', 'image/jpeg'].includes(asset.mime_type)
              ? 'image'
              : account.platform === 'instagram' &&
                  ['image/png', 'image/jpeg'].includes(asset.mime_type)
                ? asset.asset_type === 'CAROUSEL'
                  ? 'carousel'
                  : asset.asset_type === 'STORY'
                    ? 'story'
                    : 'image'
                : 'reel';
        const preferred = account.platform === 'tiktok' ? tiktokMode : 'DIRECT';
        const compatibleVideo =
          asset.mime_type === 'video/mp4' &&
          asset.width === 1080 &&
          asset.height === 1920;
        const compatibleInstagramImage =
          account.platform === 'instagram' &&
          ['image/png', 'image/jpeg'].includes(asset.mime_type) &&
          asset.width === 1080 &&
          (asset.asset_type === 'STORY'
            ? asset.height === 1920
            : asset.height !== null &&
              asset.height >= 1080 &&
              asset.height <= 1350) &&
          (!carousel || asset.mime_type === 'image/jpeg');
        const compatibleFacebookImage =
          account.platform === 'facebook' &&
          ['image/png', 'image/jpeg'].includes(asset.mime_type) &&
          !carousel &&
          asset.asset_type !== 'STORY' &&
          asset.width === 1080 &&
          asset.height !== null &&
          asset.height >= 1080 &&
          asset.height <= 1350;
        const compatibleTikTokPhoto =
          tiktokPhoto &&
          (!carousel || asset.mime_type === 'image/jpeg') &&
          asset.width === 1080 &&
          asset.height !== null &&
          asset.height >= 1080 &&
          asset.height <= 1920 &&
          asset.size_bytes <= 20_000_000;
        const mode =
          (!compatibleVideo &&
            !compatibleInstagramImage &&
            !compatibleFacebookImage &&
            !compatibleTikTokPhoto) ||
          (preferred !== 'MANUAL' &&
            account.capabilities[feature] !== 'AVAILABLE')
            ? 'MANUAL'
            : preferred;
        try {
          const result = await api.social.schedule(session, {
            contentId: asset.content_id,
            accountId,
            assetId: asset.id,
            caption,
            masterScheduledAt,
            timezone,
            mode,
            privacyLevel:
              account.platform === 'tiktok' && mode === 'DIRECT'
                ? privacy
                : undefined,
            consent: account.development_publish_test
              ? metaTestConsent
              : account.platform === 'tiktok'
                ? consent
                : true,
            disableComment,
            disableDuet,
            disableStitch,
            idempotencyKey: batchKey.current + ':' + accountId,
          });
          outcomes.push(
            names[account.platform] +
              ': ' +
              (statusNames[result.status] ?? result.status),
          );
        } catch (cause) {
          failed = true;
          outcomes.push(
            names[account.platform] +
              ': ' +
              (cause instanceof Error ? cause.message : 'falhou'),
          );
        }
      }
      if (!failed) {
        batchKey.current = crypto.randomUUID();
        setMetaTestConsent(false);
      }
      return outcomes.join(' · ');
    }, 'Solicitações de agendamento registradas.');
  }
  return (
    <div className="app-shell social-shell">
      <header>
        <Logo />
        <div className="header-actions">
          <button className="secondary" onClick={onBack}>
            Voltar ao painel
          </button>
        </div>
      </header>
      <main className="social-main">
        <div className="social-heading">
          <p className="eyebrow">MISSÃO 4 · CANAIS</p>
          <h1>Conexões e publicações</h1>
          <p>
            Um horário mestre, um estado independente para cada plataforma.
            Nenhum token é exibido.
          </p>
        </div>
        {error && (
          <div className="error-banner" role="alert">
            {error}
            <button onClick={() => setError(null)}>Fechar</button>
          </div>
        )}
        {notice && (
          <div className="social-notice" role="status">
            {notice}
          </div>
        )}
        <section className="social-grid">
          <article className="panel social-panel">
            <div className="panel-title">
              <div>
                <p className="eyebrow">OAUTH OFICIAL</p>
                <h2>Contas</h2>
              </div>
              <button
                className="secondary"
                onClick={() => void load()}
                disabled={busy}
              >
                Atualizar
              </button>
            </div>
            {(['instagram', 'facebook', 'tiktok'] as const).map((service) => {
              const found = accounts.filter(
                (account) => account.platform === service,
              );
              return (
                <div className="social-account" key={service}>
                  <div className="social-account-head">
                    <strong>{names[service]}</strong>
                    <button
                      className="secondary"
                      disabled={busy}
                      onClick={() => void connect(service)}
                    >
                      Conectar / reconectar
                    </button>
                  </div>
                  {found.length === 0 ? (
                    <p>
                      Não conectado. Configure o Developer App para habilitar
                      OAuth.
                    </p>
                  ) : (
                    found.map((account) => (
                      <div key={account.id} className="social-account-detail">
                        <strong>
                          {account.display_name || names[service]} ·{' '}
                          {account.account_type}
                        </strong>
                        <p>
                          {account.status} · App: {account.app_review_status} ·
                          Token:{' '}
                          {account.token_expires_at
                            ? new Date(account.token_expires_at).toLocaleString(
                                'pt-BR',
                              )
                            : 'sem expiração informada'}
                        </p>
                        {account.development_publish_test && (
                          <p>Conta incluída na homologação de publicação</p>
                        )}
                        <p>Escopos: {account.scopes.join(', ') || 'nenhum'}</p>
                        {service === 'instagram' && (
                          <div className="social-capabilities">
                            <label>
                              JPEG final para diagnóstico (somente leitura)
                              <select
                                value={readinessAssetId}
                                onChange={(event) =>
                                  setReadinessAssetId(event.target.value)
                                }
                              >
                                <option value="">Selecione</option>
                                {assets
                                  .filter(
                                    (item) => item.mime_type === 'image/jpeg',
                                  )
                                  .map((item) => (
                                    <option value={item.id} key={item.id}>
                                      {item.content_id?.slice(0, 8)} ·{' '}
                                      {item.width}×{item.height}
                                    </option>
                                  ))}
                              </select>
                            </label>
                            <button
                              className="secondary"
                              disabled={busy || !readinessAssetId}
                              onClick={() =>
                                void checkInstagramReadiness(account.id)
                              }
                            >
                              Verificar conexão e mídia sem publicar
                            </button>
                            {readiness?.data.accountId === account.id && (
                              <div role="status">
                                <p>Solicitação: {readiness.data.requestId}</p>
                                <p>
                                  Token válido pela Meta:{' '}
                                  {readiness.data.graph?.tokenValid
                                    ? 'sim'
                                    : 'não comprovado'}
                                  {' · '}Permissões necessárias:{' '}
                                  {readiness.data.graph?.requiredScopesGranted
                                    ? 'concedidas'
                                    : 'não comprovadas'}
                                  {readiness.data.graph?.graphHttpStatus !==
                                    null &&
                                  readiness.data.graph?.graphHttpStatus !==
                                    undefined
                                    ? ` · Graph HTTP ${readiness.data.graph.graphHttpStatus}${readiness.data.graph.providerCode === null ? '' : ` · código ${readiness.data.graph.providerCode}`} · etapa ${readiness.data.graph.failedStage ?? 'desconhecida'}`
                                    : ''}
                                </p>
                                <p>
                                  Página:{' '}
                                  {readiness.data.graph?.pageName ??
                                    'não confirmada'}
                                  {' · '}Vínculo salvo:{' '}
                                  {readiness.data.pageAccountId
                                    ? 'confirmado'
                                    : 'não confirmado'}
                                  {' · '}Instagram profissional:{' '}
                                  {readiness.data.graph?.professionalAccount
                                    ? 'confirmado'
                                    : 'não confirmado'}
                                  {' · '}Pauta:{' '}
                                  {readiness.data.editorialStatus ??
                                    'não vinculada'}
                                </p>
                                <p>
                                  Scopes efetivos:{' '}
                                  {readiness.data.graph?.grantedScopes.join(
                                    ', ',
                                  ) || 'não confirmados'}
                                </p>
                                <p>
                                  URL assinada:{' '}
                                  {readiness.data.media
                                    ? `gerada (${readiness.data.media.url.startsWith('https://') ? 'HTTPS' : 'URL não HTTPS'}; expira ${readiness.data.media.expiresAt})`
                                    : 'não configurada'}
                                </p>
                                <p>
                                  JPEG externo:{' '}
                                  {readiness.mediaCheck
                                    ? `HTTP ${readiness.mediaCheck.httpStatus}, MIME ${readiness.mediaCheck.mimeMatches ? 'OK' : 'falhou'}, bytes ${readiness.mediaCheck.bytesMatch ? 'OK' : 'falhou'}, SHA-256 ${readiness.mediaCheck.sha256Matches ? 'OK' : 'falhou'}, URL expirada HTTP ${readiness.mediaCheck.expiredHttpStatus}`
                                    : 'não validado'}
                                </p>
                              </div>
                            )}
                          </div>
                        )}
                        {!account.policy.publishing_enabled && (
                          <p>
                            Publicação externa bloqueada pela política desta
                            conta.
                          </p>
                        )}
                        <div className="social-capabilities">
                          {Object.entries(account.capabilities).map(
                            ([name, state]) => (
                              <span
                                className={
                                  'social-cap ' +
                                  (state === 'AVAILABLE' ? 'available' : '')
                                }
                                key={name}
                              >
                                {capabilityNames[name] ?? name}:{' '}
                                {statusNames[state] ?? state}
                              </span>
                            ),
                          )}
                        </div>
                        <PolicyEditor
                          account={account}
                          busy={busy}
                          onSave={async (policy) => {
                            await act(
                              () =>
                                api.social.savePolicy(
                                  session,
                                  account.id,
                                  policy,
                                ),
                              'Política salva e auditada.',
                            );
                          }}
                        />
                        <button
                          className="text-button"
                          disabled={busy}
                          onClick={() =>
                            void act(
                              () => api.social.disconnect(session, account.id),
                              'Conta desconectada. Revogue também no portal da plataforma.',
                            )
                          }
                        >
                          Desconectar
                        </button>
                      </div>
                    ))
                  )}
                </div>
              );
            })}
          </article>
          <article className="panel social-panel">
            <p className="eyebrow">PUBLICADOR SINCRONIZADO</p>
            <h2>Agendar</h2>
            <p>
              Vídeos finais validados podem seguir para Reels ou TikTok. Fotos
              JPEG e carrosséis de três páginas JPEG podem seguir ao TikTok após
              verificação do domínio; fotos de Página e carrosséis do Instagram
              exigem as permissões respectivas. Selecione a capa do carrossel
              para agendar o conjunto.
            </p>
            <label>
              Asset final
              <select
                value={assetId}
                onChange={(event) => setAssetId(event.target.value)}
              >
                <option value="">Selecione</option>
                {assets.map((item) => (
                  <option value={item.id} key={item.id}>
                    {item.asset_type} · {item.content_id?.slice(0, 8)} ·{' '}
                    {item.width}×{item.height} · {item.mime_type}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Legenda
              <textarea
                value={caption}
                maxLength={2200}
                onChange={(event) => setCaption(event.target.value)}
                rows={4}
              />
            </label>
            <fieldset>
              <legend>Contas de destino</legend>
              {accounts
                .filter((account) => account.status === 'CONNECTED')
                .map((account) => (
                  <label className="social-check" key={account.id}>
                    <input
                      type="checkbox"
                      checked={selected.includes(account.id)}
                      onChange={(event) =>
                        setSelected(
                          event.target.checked
                            ? [...selected, account.id]
                            : selected.filter((id) => id !== account.id),
                        )
                      }
                    />
                    {names[account.platform]} · {account.display_name}
                  </label>
                ))}
            </fieldset>
            <label>
              Data do horário mestre
              <input
                type="date"
                value={masterDate}
                onChange={(event) => setMasterDate(event.target.value)}
              />
            </label>
            <label>
              Hora do horário mestre
              <input
                type="time"
                value={masterClock}
                onChange={(event) => setMasterClock(event.target.value)}
              />
            </label>
            <label>
              Fuso horário IANA (explícito)
              <input
                value={timezone}
                onChange={(event) => setTimezone(event.target.value)}
                placeholder="America/Sao_Paulo"
                list="social-timezones"
              />
            </label>
            <datalist id="social-timezones">
              <option value="America/Sao_Paulo" />
              <option value="UTC" />
            </datalist>
            {tiktokSelected && (
              <div className="social-tiktok">
                <h3>TikTok · controles por publicação</h3>
                <label>
                  Fluxo
                  <select
                    value={tiktokMode}
                    onChange={(event) =>
                      setTikTokMode(
                        event.target.value as 'DIRECT' | 'DRAFT' | 'MANUAL',
                      )
                    }
                  >
                    <option value="DRAFT">
                      Enviar para finalizar no TikTok
                    </option>
                    <option value="DIRECT">Direct Post (app auditado)</option>
                    <option value="MANUAL">Somente preparar manualmente</option>
                  </select>
                </label>
                {tiktokMode === 'DIRECT' && (
                  <>
                    <label>
                      Privacidade
                      <select
                        value={privacy}
                        onChange={(event) => setPrivacy(event.target.value)}
                      >
                        <option value="">Selecione</option>
                        {creatorInfo?.privacy_level_options.map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="social-check">
                      <input
                        type="checkbox"
                        checked={disableComment}
                        onChange={(event) =>
                          setDisableComment(event.target.checked)
                        }
                        disabled={creatorInfo?.comment_disabled}
                      />
                      Desabilitar comentários
                    </label>
                    {asset?.mime_type === 'video/mp4' && (
                      <label className="social-check">
                        <input
                          type="checkbox"
                          checked={disableDuet}
                          onChange={(event) =>
                            setDisableDuet(event.target.checked)
                          }
                          disabled={creatorInfo?.duet_disabled}
                        />
                        Desabilitar duet
                      </label>
                    )}
                    {asset?.mime_type === 'video/mp4' && (
                      <label className="social-check">
                        <input
                          type="checkbox"
                          checked={disableStitch}
                          onChange={(event) =>
                            setDisableStitch(event.target.checked)
                          }
                          disabled={creatorInfo?.stitch_disabled}
                        />
                        Desabilitar stitch
                      </label>
                    )}
                  </>
                )}
                <label className="social-check">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(event) => setConsent(event.target.checked)}
                  />
                  Autorizo este envio específico ao TikTok
                </label>
              </div>
            )}
            {metaTestSelected && (
              <label className="social-check">
                <input
                  type="checkbox"
                  checked={metaTestConsent}
                  onChange={(event) => setMetaTestConsent(event.target.checked)}
                />
                Autorizo este agendamento de homologação nas contas Meta
                selecionadas
              </label>
            )}
            <button
              className="primary"
              disabled={
                busy ||
                !asset ||
                !selected.length ||
                (tiktokSelected && tiktokMode !== 'MANUAL' && !consent) ||
                (metaTestSelected && !metaTestConsent)
              }
              onClick={() => void schedule()}
            >
              Agendar destinos selecionados
            </button>
          </article>
        </section>
        <section className="panel social-panel">
          <div className="panel-title">
            <div>
              <p className="eyebrow">POR PLATAFORMA</p>
              <h2>Histórico e reconciliação</h2>
            </div>
            <button
              className="secondary"
              onClick={() => void load()}
              disabled={busy}
            >
              Atualizar
            </button>
          </div>
          {publications.length === 0 ? (
            <p>Nenhuma publicação registrada.</p>
          ) : (
            <div className="social-history">
              {publications.map((item) => (
                <div className="social-history-item" key={item.id}>
                  <div>
                    <strong>
                      {names[item.platform]} ·{' '}
                      {statusNames[item.delivery_status] ??
                        item.delivery_status}
                    </strong>
                    <p>
                      {item.caption.slice(0, 120) || 'Sem legenda'} ·{' '}
                      {item.publish_mode}
                    </p>
                    <small>
                      Mestre:{' '}
                      {item.master_scheduled_at
                        ? new Date(item.master_scheduled_at).toLocaleString(
                            'pt-BR',
                          )
                        : '—'}{' '}
                      · Dispatch:{' '}
                      {item.dispatched_at
                        ? new Date(item.dispatched_at).toLocaleString('pt-BR')
                        : '—'}{' '}
                      · Aceito:{' '}
                      {item.accepted_at
                        ? new Date(item.accepted_at).toLocaleString('pt-BR')
                        : '—'}{' '}
                      · Publicado:{' '}
                      {item.published_at
                        ? new Date(item.published_at).toLocaleString('pt-BR')
                        : '—'}
                    </small>
                    {item.last_error && (
                      <p>Último erro: {item.last_error.code}</p>
                    )}
                    {item.delivery_status === 'PUBLISHED' && (
                      <p>
                        Métricas · última coleta:{' '}
                        {item.last_metrics_sync_at
                          ? new Date(item.last_metrics_sync_at).toLocaleString(
                              'pt-BR',
                            )
                          : 'ainda não coletadas'}
                        {' · '}próxima:{' '}
                        {item.next_metrics_sync_at
                          ? new Date(item.next_metrics_sync_at).toLocaleString(
                              'pt-BR',
                            )
                          : 'não agendada'}
                        {item.metrics_sync_error?.code &&
                          ' · erro: ' + item.metrics_sync_error.code}
                      </p>
                    )}
                    {metrics[item.id] && (
                      <p>
                        Métricas:{' '}
                        {(metrics[item.id] ?? [])
                          .map(
                            (metric) =>
                              metric.metric_name + ': ' + metric.provider_value,
                          )
                          .join(' · ') || 'não disponíveis'}
                      </p>
                    )}
                    {comments[item.id] && (
                      <div>
                        <strong>Comentários autorizados</strong>
                        {(comments[item.id] ?? []).length === 0 ? (
                          <p>Nenhum comentário disponível.</p>
                        ) : (
                          <ul>
                            {(comments[item.id] ?? []).map((comment) => (
                              <li key={comment.provider_comment_id}>
                                {comment.body}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="social-history-actions">
                    {item.last_error?.retryable &&
                      !item.last_error.ambiguous && (
                        <button
                          className="secondary"
                          disabled={busy}
                          onClick={() =>
                            void act(
                              () => api.social.retry(session, item.id),
                              'Retry solicitado.',
                            )
                          }
                        >
                          Reprocessar
                        </button>
                      )}
                    {item.delivery_status === 'PUBLISHED' && (
                      <button
                        className="secondary"
                        onClick={() =>
                          void api.social
                            .metrics(session, item.id)
                            .then((data) =>
                              setMetrics((previous) => ({
                                ...previous,
                                [item.id]: data.metrics,
                              })),
                            )
                            .catch((cause: unknown) =>
                              setError(
                                cause instanceof Error
                                  ? cause.message
                                  : 'Falha nas métricas.',
                              ),
                            )
                        }
                      >
                        Métricas
                      </button>
                    )}
                    {item.delivery_status === 'PUBLISHED' &&
                      accounts.find(
                        (account) => account.id === item.platform_account_id,
                      )?.capabilities.comments === 'AVAILABLE' && (
                        <button
                          className="secondary"
                          disabled={busy}
                          onClick={() =>
                            void act(async () => {
                              await api.social.syncComments(session, item.id);
                              const data = await api.social.comments(
                                session,
                                item.id,
                              );
                              setComments((previous) => ({
                                ...previous,
                                [item.id]: data.comments,
                              }));
                            }, 'Comentários atualizados sem respostas automáticas.')
                          }
                        >
                          Ler comentários
                        </button>
                      )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
